import { useState, useMemo, useEffect } from 'react';
import { PROSPECTS_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Button } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useUserIdentity } from '@lucid/portal-core';
import { useLabels } from '../../../hooks/useLabels';
import { resolveEffectiveLocale } from '../../../utils/tools';
import Configurator from '../../../components/Configurator/Configurator';
import type { ConfiguratorProps } from '../../../components/Configurator/Configurator';
import { useProspectsTable } from './hooks/useProspectsTable';
import { useProspectUrlState } from './hooks/useProspectUrlState';
import Checkout from '../../../components/Checkout/Checkout';
import type { InventoryTableRow } from '../../../components/Configurator/utils';
import { useGetQuoteById } from '../../../api/services/lupp/quotes/quotes';
import ProspectsListView from './ProspectsListView';

type Theme = 'dark' | 'light';
type Labels = Record<string, string> | undefined;

type ConfiguratorPanelProps = Pick<
  ConfiguratorProps,
  'checkoutParams' | 'onCheckoutNavigate' | 'onShortcodeNavigate' | 'prospectDetails'
> & {
  theme: Theme;
  labels: Labels;
  prospectName: string;
  hideHeader: boolean;
  onBackToProspects: () => void;
  onDetailsViewChange: (isShowingDetails: boolean) => void;
  onCheckoutBack: () => void;
  onQuoteCreated: (quoteId: string) => void;
};

/** Renders the vehicle configurator, with an optional header for navigating back to the prospect list. */
const ConfiguratorPanel: React.FC<ConfiguratorPanelProps> = ({
  theme,
  labels,
  prospectName,
  hideHeader,
  onBackToProspects,
  checkoutParams,
  onCheckoutNavigate,
  onShortcodeNavigate,
  onDetailsViewChange,
  onCheckoutBack,
  prospectDetails,
  onQuoteCreated,
}) => (
  <Box>
    {!hideHeader && (
      <Box mb={16}>
        <Button
          type="tertiary"
          color="neutral"
          theme={theme}
          dataTestId="back-to-prospects-button"
          gaComponent="prospects"
          label={labels?.['list.backButton'] ?? 'Back to Prospects'}
          iconLeft={{ category: 'arrows', name: 'arrow-left' }}
          handleClick={onBackToProspects}
        />
      </Box>
    )}
    {!hideHeader && (
      <>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {prospectName
            ? (labels?.['prospects.quotationTitle.withName']?.replace(
                '{{prospectName}}',
                prospectName
              ) ?? `Quotation for ${prospectName}`)
            : (labels?.['prospects.quotationTitle.default'] ?? 'Quotation')}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['prospects.savedQuotationsNote'] ??
            'Saved quotations can be reviewed in prospect details.'}
        </Typography>
      </>
    )}
    <Box pt={hideHeader ? 0 : 24}>
      <Configurator
        dataTestId="prospect-configurator"
        showConfigureTab
        onDetailsViewChange={onDetailsViewChange}
        checkoutParams={checkoutParams}
        onCheckoutNavigate={onCheckoutNavigate}
        onShortcodeNavigate={onShortcodeNavigate}
        onCheckoutBack={onCheckoutBack}
        prospectDetails={prospectDetails}
        onQuoteCreated={onQuoteCreated}
      />
    </Box>
  </Box>
);

const Prospects: React.FC = () => {
  const { userIdentity } = useUserIdentity();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const selectedQuote = useStore((state) => state.selectedQuote);
  const clearSelectedProspect = useStore((state) => state.clearSelectedProspect);
  const clearSelectedQuote = useStore((state) => state.clearSelectedQuote);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);

  // Labels - with language-based locale selection using actual user's country code
  const effectiveLocale = resolveEffectiveLocale(language, userIdentity?.countryCode, locale);

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: PROSPECTS_LABELS_ID,
    locale: effectiveLocale,
    countryCode: userIdentity?.countryCode,
    language: null,
  });

  // URL state management
  const {
    selectedProspectId,
    activeTab,
    showConfigurator,
    showCheckout,
    checkoutParams,
    quoteIdFromUrl,
    hasInitialProspectInUrl,
    selectedProspectData,
    onProspectSelect,
    onTabChange,
    onCreateQuotation,
    onCreateProspect,
    onViewAllTestDrives,
    onBackToProspects,
    onCheckoutNavigate,
    onShortcodeNavigate,
    onCheckoutBack,
    onBackToProspectDetails,
    onQuoteClick,
  } = useProspectUrlState();

  // Table state management - disable when in configurator/checkout view
  const {
    data: prospectsData,
    totalCount: prospectsTotalCount,
    isLoading,
    isFetching,
    error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onSort,
    filters,
    onFiltersChange,
  } = useProspectsTable({
    enabled: !labelsLoading && !labelsError && !showConfigurator,
  });

  // Merge table data with single prospect view data
  const tableData = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return [selectedProspectData.prospectDetails];
    }
    return prospectsData;
  }, [prospectsData, hasInitialProspectInUrl, selectedProspectData]);

  const totalCount = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return 1;
    }
    return prospectsTotalCount;
  }, [hasInitialProspectInUrl, selectedProspectData, prospectsTotalCount]);

  // Configurator handlers
  const handleBackToProspects = () => {
    setShowVehicleDetails(false);
    clearSelectedProspect();
    onBackToProspects();
  };

  const handleCheckoutBack = () => {
    setShowVehicleDetails(false);
    onCheckoutBack();
  };

  // Clear selected quote on mount
  useEffect(() => {
    clearSelectedQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToInventory = () => {
    clearSelectedQuote();
    onBackToProspectDetails('quotations');
  };

  const prospectId = selectedProspectId ?? '';
  const firstName = selectedProspectData?.prospectDetails?.prospectFirstName ?? '';
  const lastName = selectedProspectData?.prospectDetails?.prospectLastName ?? '';
  const email = selectedProspectData?.prospectDetails?.prospectEmail ?? '';
  const prospectName = firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  // Use quoteId from URL (for refresh) or from selected quote (from click)
  const quoteId = quoteIdFromUrl ?? selectedQuote?.quote_identifier ?? '';

  const { data: quoteData, isLoading: isQuoteLoading } = useGetQuoteById({
    prospectId,
    quoteId,
    enabled: !!prospectId && !!quoteId,
  });

  const detailsVehicle = {
    vin: quoteData?.vin ?? '',
    batchNumber: quoteData?.vinBatchNumber ?? '',
    year: quoteData?.modelYear ?? '',
  } as InventoryTableRow;

  const prospectDetails = {
    prospectId,
    prospectName,
    quoteId,
    firstName,
    lastName,
    email,
    stateProvince: selectedProspectData?.prospectDetails?.prospectAddressDetail?.stateProvince,
  };

  // Show checkout when we have a quote (from URL or from selection)
  if (selectedQuote || quoteIdFromUrl) {
    return (
      <Checkout
        vehicle={detailsVehicle}
        checkoutData={quoteData ?? null}
        isLoading={isQuoteLoading}
        onBack={handleBackToInventory}
        dataTestId={`${quoteId}-checkout`}
        prospectDetails={prospectDetails}
        docusignStatus={quoteData?.docusignStatus}
        currentStatus={quoteData?.currentStatus}
        onQuoteCreated={onQuoteClick}
      />
    );
  }

  // Render configurator view
  if (showConfigurator) {
    return (
      <ConfiguratorPanel
        theme={theme}
        labels={labels}
        prospectName={prospectName}
        hideHeader={showVehicleDetails || showCheckout}
        onBackToProspects={handleBackToProspects}
        checkoutParams={checkoutParams}
        onCheckoutNavigate={onCheckoutNavigate}
        onShortcodeNavigate={onShortcodeNavigate}
        onDetailsViewChange={setShowVehicleDetails}
        onCheckoutBack={handleCheckoutBack}
        prospectDetails={prospectDetails}
        onQuoteCreated={onQuoteClick}
      />
    );
  }

  return (
    <ProspectsListView
      theme={theme}
      labels={labels}
      labelsLoading={labelsLoading}
      labelsError={labelsError}
      isLoading={isLoading}
      error={error}
      isFetching={isFetching}
      searchQuery={searchQuery}
      debouncedSearch={debouncedSearch}
      tableData={tableData}
      totalCount={totalCount}
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearchChange={onSearchChange}
      onCreateQuotation={onCreateQuotation}
      onCreateProspect={onCreateProspect}
      onViewAllTestDrives={onViewAllTestDrives}
      sortColumn={sortField}
      sortDirection={sortOrder}
      onSort={onSort}
      filters={filters}
      onFiltersChange={onFiltersChange}
      selectedProspectId={selectedProspectId}
      onProspectSelect={onProspectSelect}
      activeTab={activeTab}
      onTabChange={onTabChange}
      onQuoteClick={onQuoteClick}
      locale={locale}
    />
  );
};

export default Prospects;
